import {
  presentationCallTarget,
  whatsappUrl,
  type Presentation,
  type PresentationContact,
  type PresentationSection,
} from '@base/shared';
import { Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitch } from '../shell/LanguageSwitch.tsx';
import { BUILT_LOGO_URL, PRESENTATION } from '../platform.tsx';
import { SISTEMA_NOME } from '../sistema.ts';

/**
 * A página de apresentação (ADR 0118): o que o endereço do sistema mostra a quem não entrou. O
 * conteúdo vem do visual.json; as cores e a letra, do tema, como o resto do sistema.
 */
export function PresentationPage({
  presentation = PRESENTATION,
}: {
  presentation?: Presentation | null;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!presentation) return;
    const before = document.title;
    document.title = `${SISTEMA_NOME} · ${presentation.titulo}`;
    return () => {
      document.title = before;
    };
  }, [presentation]);
  if (!presentation) return null;

  const call = presentationCallTarget(presentation);
  // Abertura ao lado mostra o logo grande; sem logo do cliente, fica centralizada.
  const side = presentation.abertura === 'lado' && !!BUILT_LOGO_URL;

  return (
    <div className="flex min-h-dvh flex-col bg-paper text-ink">
      <header className="border-b border-rule bg-sheet">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          {BUILT_LOGO_URL ? (
            <img
              src={BUILT_LOGO_URL}
              alt={SISTEMA_NOME}
              className="max-h-10 w-auto max-w-40 object-contain object-left"
            />
          ) : (
            <span className="truncate text-lg font-semibold">{SISTEMA_NOME}</span>
          )}
          <Link
            to="/login"
            className="ui-button-secondary rounded-md px-4 py-1.5 text-sm font-medium text-ink"
          >
            {t('presentation.signIn')}
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-24">
          <div
            className={
              side
                ? 'grid items-center gap-10 md:grid-cols-[3fr_2fr]'
                : 'mx-auto flex max-w-2xl flex-col items-center text-center'
            }
          >
            <div className={`flex flex-col gap-5 ${side ? '' : 'items-center'}`}>
              <h1 className="text-3xl font-semibold tracking-tight text-balance sm:text-5xl">
                {presentation.titulo}
              </h1>
              {presentation.texto && (
                <p className="text-lg text-pretty text-muted">{presentation.texto}</p>
              )}
              {call && (
                <a
                  href={call}
                  {...(call.startsWith('http')
                    ? { target: '_blank', rel: 'noopener noreferrer' }
                    : {})}
                  className="w-fit rounded-md bg-accent px-6 py-3 text-base font-medium text-on-accent shadow-sm hover:opacity-90"
                >
                  {presentation.chamada}
                </a>
              )}
            </div>
            {side && (
              <div className="hidden items-center justify-center rounded-lg border border-rule bg-sheet p-10 md:flex">
                <img
                  src={BUILT_LOGO_URL ?? undefined}
                  alt=""
                  className="max-h-40 w-auto max-w-full object-contain"
                />
              </div>
            )}
          </div>
        </section>

        {presentation.secoes.map((section, i) => (
          <Section key={i} section={section} tinted={i % 2 === 0} />
        ))}

        {presentation.contato && (
          <Contact contact={presentation.contato} tinted={presentation.secoes.length % 2 === 0} />
        )}
      </main>

      <footer className="border-t border-rule bg-sheet">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-6 text-sm text-muted sm:px-6">
          <span>
            © {new Date().getFullYear()} {SISTEMA_NOME}
          </span>
          <LanguageSwitch />
        </div>
      </footer>
    </div>
  );
}

function Section({ section, tinted }: { section: PresentationSection; tinted: boolean }) {
  return (
    <section className={tinted ? 'border-y border-rule bg-sheet' : ''}>
      <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-14 sm:px-6">
        <h2 className="text-2xl font-semibold tracking-tight">{section.titulo}</h2>
        {section.tipo === 'texto' && (
          <p className="max-w-3xl text-base leading-relaxed whitespace-pre-line text-muted">
            {section.texto}
          </p>
        )}
        {section.tipo === 'destaques' && (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {section.itens.map((item, i) => (
              <li
                key={i}
                className="flex flex-col gap-2 rounded-lg border border-rule bg-paper p-5"
              >
                <span className="h-1 w-8 rounded-full bg-accent" aria-hidden />
                <h3 className="font-semibold">{item.titulo}</h3>
                {item.texto && <p className="text-sm text-muted">{item.texto}</p>}
              </li>
            ))}
          </ul>
        )}
        {section.tipo === 'passos' && (
          <ol className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {section.itens.map((item, i) => (
              <li key={i} className="flex gap-4">
                <span
                  className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-on-accent"
                  aria-hidden
                >
                  {i + 1}
                </span>
                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold">{item.titulo}</h3>
                  {item.texto && <p className="text-sm text-muted">{item.texto}</p>}
                </div>
              </li>
            ))}
          </ol>
        )}
        {section.tipo === 'perguntas' && (
          <div className="flex max-w-3xl flex-col divide-y divide-rule rounded-lg border border-rule bg-paper">
            {section.itens.map((item, i) => (
              <details key={i} className="group px-5 py-4">
                <summary className="cursor-pointer font-medium marker:text-muted">
                  {item.titulo}
                </summary>
                {item.texto && <p className="mt-2 text-sm text-muted">{item.texto}</p>}
              </details>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Contact({ contact, tinted }: { contact: PresentationContact; tinted: boolean }) {
  const { t } = useTranslation();
  const rows: { label: string; value: string; href?: string }[] = [];
  if (contact.whatsapp)
    rows.push({
      label: t('presentation.whatsapp'),
      value: phoneLabel(contact.whatsapp),
      href: whatsappUrl(contact.whatsapp),
    });
  if (contact.telefone)
    rows.push({
      label: t('presentation.phone'),
      value: phoneLabel(contact.telefone),
      href: `tel:+${contact.telefone}`,
    });
  if (contact.email)
    rows.push({
      label: t('presentation.email'),
      value: contact.email,
      href: `mailto:${contact.email}`,
    });
  if (contact.endereco) rows.push({ label: t('presentation.address'), value: contact.endereco });
  if (contact.horario) rows.push({ label: t('presentation.hours'), value: contact.horario });

  return (
    <section
      id="contato"
      className={`scroll-mt-4 ${tinted ? 'border-y border-rule bg-sheet' : ''}`}
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-14 sm:px-6">
        <h2 className="text-2xl font-semibold tracking-tight">{t('presentation.contact')}</h2>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((row) => (
            <div
              key={row.label}
              className={`flex flex-col gap-1 rounded-lg border border-rule p-5 ${tinted ? 'bg-paper' : 'bg-sheet'}`}
            >
              <dt className="text-xs font-medium tracking-wide text-muted uppercase">
                {row.label}
              </dt>
              <dd className="break-words">
                {row.href ? (
                  <a
                    href={row.href}
                    {...(row.href.startsWith('http')
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                    className="text-brand-text underline-offset-2 hover:underline"
                  >
                    {row.value}
                  </a>
                ) : (
                  row.value
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/** 5531999999999 → +55 31 99999-9999 (o jeito brasileiro; outros países saem só com o +). */
export function phoneLabel(number: string): string {
  const br = /^55(\d{2})(\d{4,5})(\d{4})$/.exec(number);
  return br ? `+55 ${br[1]} ${br[2]}-${br[3]}` : `+${number}`;
}
